/**
 * Shared helpers for fetching sponsorship tokens (access + extension) from an
 * integrator's backend endpoints.
 *
 * The integrator's token routes return their failure reason in the JSON body
 * (`{ error: "createSponsorshipSigner: missing env var(s): ..." }`). Surfacing
 * only the HTTP status — "Failed to fetch access token (500 ...)" — hides that
 * reason and turns every misconfiguration into the same opaque dialog error.
 * These helpers read the body and fold the server's message into the thrown
 * Error so the actual cause (missing env var, bad JWK, expired session) is
 * visible at the call site and in the dialog.
 */

/**
 * Pull a human-readable reason out of a failed token response. Tries the JSON
 * `error`/`message` fields first, then falls back to a short text snippet, then
 * to the HTTP status text. Never throws — diagnostics must not mask the original
 * failure with a parse error.
 */
async function extractServerReason(response: Response): Promise<string> {
  try {
    // Clone so a JSON parse failure can still fall back to reading text.
    const text = await response.clone().text();
    if (!text) return response.statusText;
    try {
      const data = JSON.parse(text) as { error?: unknown; message?: unknown };
      const reason = data.error ?? data.message;
      if (typeof reason === "string" && reason.trim()) return reason;
    } catch {
      // Body wasn't JSON — fall through to the raw text snippet.
    }
    // Cap the snippet so an HTML error page doesn't flood the message.
    return text.slice(0, 200);
  } catch {
    return response.statusText;
  }
}

/**
 * Build an Error for a failed token fetch that includes the server's reason.
 * `label` names the token kind, e.g. "access token" or "extension token".
 */
export async function tokenFetchError(
  label: string,
  response: Response,
): Promise<Error> {
  const reason = await extractServerReason(response);
  const suffix = reason ? `: ${reason}` : "";
  return new Error(
    `Failed to fetch ${label} (${response.status} ${response.statusText})${suffix}`,
  );
}
